import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("ld_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("ld_user", JSON.stringify(data));
    } catch {
      setUser(null);
      localStorage.removeItem("ld_token");
      localStorage.removeItem("ld_user");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const signin = async (email, password, locationId = null) => {
    const payload = { email, password };
    if (locationId) payload.location_id = locationId;
    const { data } = await api.post("/auth/signin", payload);
    localStorage.setItem("ld_token", data.token);
    localStorage.setItem("ld_user", JSON.stringify(data.user));
    if (data.user?.active_location_id) {
      localStorage.setItem("ld_active_location_id", data.user.active_location_id);
    }
    setUser(data.user);
    return data.user;
  };

  const signup = async (name, email, password, locationData = {}, ref = null) => {
    const payload = {
      name,
      email,
      password,
      location_name: locationData.location_name || `${name}'s Studio`,
      city: locationData.city || "",
      state: locationData.state || "",
      ref: ref || locationData.ref || undefined,
    };
    const { data } = await api.post("/auth/signup", payload);
    if (data.token && !data.requires_verification) {
      localStorage.setItem("ld_token", data.token);
      localStorage.setItem("ld_user", JSON.stringify(data.user));
      if (data.location?.id) {
        localStorage.setItem("ld_active_location_id", data.location.id);
      } else if (data.user?.active_location_id) {
        localStorage.setItem("ld_active_location_id", data.user.active_location_id);
      }
      setUser(data.user);
    }
    return data;
  };

  const verifyEmail = async (email, { otp, token } = {}) => {
    const payload = { email };
    if (otp) payload.otp = otp;
    if (token) payload.token = token;
    const { data } = await api.post("/auth/verify-email", payload);
    if (data.token) {
      localStorage.setItem("ld_token", data.token);
      localStorage.setItem("ld_user", JSON.stringify(data.user));
      if (data.user?.active_location_id) {
        localStorage.setItem("ld_active_location_id", data.user.active_location_id);
      }
      setUser(data.user);
    }
    return data;
  };

  const resendVerification = async (email) => {
    const { data } = await api.post("/auth/resend-verification", { email });
    return data;
  };

  const signout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.clear();
    setUser(null);
  };

  const updateProfile = async (patch) => {
    const { data } = await api.patch("/auth/me", patch);
    setUser(data);
    localStorage.setItem("ld_user", JSON.stringify(data));
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, signin, signup, verifyEmail, resendVerification, signout, updateProfile, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
